import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {PointMaskProps} from '@Panels/masking-panel/masking-modal.types';
import FreehandMaskingCanvas from '@Panels/masking-panel/components/freehand-masking-canvas';
import FreehandMaskingEditingPanel from '@Panels/masking-panel/components/freehand-masking-editing-panel';
import {FreehandMaskingEditingType} from '@Panels/masking-panel/components/freehand-masking-editing-panel/freehand-masking-editing-panel';
import {
  setColorPop,
  setCurvedJoints,
  setRemoveBackround,
  toggleIsBackgroundRemoveDisabled,
} from '@Panels/masking-panel/components/freehand-masking-panel/freehand-masking-panel-reducer';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import type {MaskOptsInterface} from '@Libraries/mask';
import {MaskingEffect} from '@PosterWhiteboard/classes/masking/masking.class';
import type {MaskingFreehandObject} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getCreditsForImageBGR, isImageBGRAllowedForPAYGUser} from '@Libraries/bg-removals-library';
import {isUserPremium} from '@Libraries/user.library';

const query: ContainerQueryObject = {
  small: {
    maxWidth: 499,
  },
};

export interface FreehandMaskingPanelProps {
  imageUrl: string;
  cropData?: CropData;
  initialMaskingData?: MaskingFreehandObject;
  className?: string;
  isBackgroundRemoved?: boolean;
  isColorPop?: boolean;
  areJointsCurved?: boolean;
  removeBackgroundCallback?: (isBackgroundRemoved: boolean, replaceImageCallback: (url: string) => void) => Promise<boolean>;
}

export const FREEHAND_MASKING_CANVAS_ID = 'freehand-canvas';

function FreehandMaskingPanel({className = '', ...props}: FreehandMaskingPanelProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, query);
  const isBGRAllowed = isUserPremium() || (isImageBGRAllowedForPAYGUser() && getCreditsForImageBGR() > 0);

  const isBackgroundRemoved = useAppSelector((state) => {
    return state.freehandMasking.backgroundRemoved;
  });

  const isColorPop = useAppSelector((state) => {
    return state.freehandMasking.colorPop;
  });

  const areJointsCurved = useAppSelector((state) => {
    return state.freehandMasking.curvedJoints;
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (props.isBackgroundRemoved) {
      dispatch(setRemoveBackround(true));
    }
    if (props.initialMaskingData?.maskEffect === MaskingEffect.COLOR_POP) {
      dispatch(setColorPop(true));
    }
    if (props.initialMaskingData?.isMaskCurved) {
      dispatch(setCurvedJoints(true));
    }
  }, []);

  const maskingCanvasProps: PointMaskProps = {
    id: FREEHAND_MASKING_CANVAS_ID,
    url: props.imageUrl,
    opts: {
      imageErrorHandler: () => {
        console.log('Image failed to load');
      },
    } as MaskOptsInterface,
  };

  const onEdited = (editType: FreehandMaskingEditingType): void => {
    if (editType === FreehandMaskingEditingType.REMOVE_BACKGROUND) {
      void onRemoveBackground();
    } else if (editType === FreehandMaskingEditingType.COLOR_POP) {
      window.pointMask?.setMaskEffect(isColorPop ? MaskingEffect.NONE : MaskingEffect.COLOR_POP);
    } else if (editType === FreehandMaskingEditingType.JOINTS) {
      window.pointMask?.setMaskCurved(!areJointsCurved);
    } else {
      throw new Error('Unexpected edit type');
    }
  };

  const onRemoveBackground = async (): Promise<void> => {
    if (isBGRAllowed && props.removeBackgroundCallback && window.pointMask) {
      dispatch(toggleIsBackgroundRemoveDisabled());
      let wasBackgroundRemoved;

      try {
        wasBackgroundRemoved = await props.removeBackgroundCallback(isBackgroundRemoved, (newImage) => {
          window.pointMask?.setBackgroundImage(newImage);
        });
      } finally {
        dispatch(toggleIsBackgroundRemoveDisabled());
      }

      if (typeof wasBackgroundRemoved !== 'undefined') {
        dispatch(setRemoveBackround(wasBackgroundRemoved));
      }
    }
  };

  const getEditingPanel = (): ReactElement => {
    return <FreehandMaskingEditingPanel onEdit={onEdited} isSmall={params.small} additionalClasses={params.small ? 'spacing-p-t-5' : 'spacing-m-r-12'} />;
  };

  const getCanvasPanel = (): ReactElement => {
    return (
      <FreehandMaskingCanvas
        id={maskingCanvasProps.id}
        url={maskingCanvasProps.url}
        isSmallScreen={params.small}
        cropData={props.cropData}
        initialMaskingData={props.initialMaskingData}
      />
    );
  };

  return (
    <div ref={containerRef} className={`${params.small ? 'flex-v-row flex-column-reverse' : 'flex-h-row'} ${className} _full-width _full-height`}>
      {getEditingPanel()}
      {getCanvasPanel()}
    </div>
  );
}

export default FreehandMaskingPanel;
